import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../api/axios";
import { FaFileInvoice, FaCheck, FaTimes, FaRedo, FaSpinner } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./ModifierFacture.css";

export default function ModifierFacture() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    numero: "",
    fournisseur: "",
    date_achat: "",
    montant_total: "",
    notes: "",
    facture_image: null,
  });
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    numero: "",
    fournisseur: "",
    date_achat: "",
    montant_total: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    numero: false,
    fournisseur: false,
    date_achat: false,
    montant_total: false,
  });

  const fetchFacture = async () => {
    try {
      const response = await api.get(`/api/factures/${id}/`);
      const clonedData = typeof structuredClone !== 'undefined'
        ? structuredClone(response.data)
        : JSON.parse(JSON.stringify(response.data));

      setFormData({
        numero: response.data.numero || "",
        fournisseur: response.data.fournisseur || "",
        date_achat: response.data.date_achat || "",
        montant_total: response.data.montant_total?.toString() || "",
        notes: response.data.notes || "",
        facture_image: null,
      });
      setInitialData(clonedData);
      setError(null);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement de la facture:", error);
      let errorMessage = "Impossible de charger la facture.";
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "La facture demandée n'existe pas.";
        } else if (error.response.status === 403) {
          errorMessage = "Vous n'avez pas la permission d'accéder à cette facture.";
        } else if (error.response.status >= 500) {
          errorMessage = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMessage = error.response.data?.detail || error.response.data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Impossible de contacter le serveur. Vérifiez votre connexion.";
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFacture();
  }, [id]);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "numero" || name === "fournisseur" || name === "date_achat" || name === "montant_total") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if ((name === "numero" || name === "fournisseur" || name === "date_achat" || name === "montant_total") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        facture_image: file,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (name === "numero" || name === "fournisseur" || name === "date_achat" || name === "montant_total") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newTouchedFields = {
      numero: true,
      fournisseur: true,
      date_achat: true,
      montant_total: true,
    };
    setTouchedFields(newTouchedFields);

    const newFieldErrors = {
      numero: validateField("numero", formData.numero),
      fournisseur: validateField("fournisseur", formData.fournisseur),
      date_achat: validateField("date_achat", formData.date_achat),
      montant_total: validateField("montant_total", formData.montant_total),
    };
    setFieldErrors(newFieldErrors);

    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");

    if (hasErrors) {
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const factureData = {
        numero: formData.numero,
        fournisseur: formData.fournisseur,
        date_achat: formData.date_achat,
        montant_total: parseFloat(formData.montant_total),
        notes: formData.notes || null,
      };

      // Si une nouvelle image est fournie, utiliser FormData
      if (formData.facture_image) {
        const formDataImage = new FormData();
        Object.keys(factureData).forEach(key => {
          const value = factureData[key];
          // Ne pas ajouter les valeurs null ou undefined à FormData
          if (value !== null && value !== undefined) {
            formDataImage.append(key, value);
          }
        });
        formDataImage.append('facture_image', formData.facture_image);
        await api.patch(`/api/factures/${id}/`, formDataImage, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.patch(`/api/factures/${id}/`, factureData);
      }

      setShowConfirmModal(false);
      toast.success("Facture modifiée avec succès !");
      setTimeout(() => {
        navigate("/factures");
      }, 500);
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(", ") ||
        "Erreur lors de la modification de la facture !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const handleReset = () => {
    if (initialData) {
      setFormData({
        numero: initialData.numero || "",
        fournisseur: initialData.fournisseur || "",
        date_achat: initialData.date_achat || "",
        montant_total: initialData.montant_total?.toString() || "",
        notes: initialData.notes || "",
        facture_image: null,
      });
      setFieldErrors({
        numero: "",
        fournisseur: "",
        date_achat: "",
        montant_total: "",
      });
      setTouchedFields({
        numero: false,
        fournisseur: false,
        date_achat: false,
        montant_total: false,
      });
      toast.info("Formulaire réinitialisé aux valeurs d'origine.");
    }
  };

  // Helper function to parse YYYY-MM-DD string as local date
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  };

  const isFormValid = () => {
    if (!formData.numero || !formData.numero.trim()) return false;
    if (!formData.fournisseur || !formData.fournisseur.trim()) return false;
    if (!formData.date_achat) return false;
    if (!formData.montant_total || parseFloat(formData.montant_total) <= 0) return false;
    return true;
  };

  const hasChanges = () => {
    if (!initialData) return false;
    return (
      formData.numero !== (initialData.numero || "") ||
      formData.fournisseur !== (initialData.fournisseur || "") ||
      formData.date_achat !== (initialData.date_achat || "") ||
      formData.montant_total !== (initialData.montant_total?.toString() || "") ||
      formData.notes !== (initialData.notes || "") ||
      formData.facture_image !== null
    );
  };

  if (loading) {
    return (
      <div className="modifier-facture-page">
        <div className="loading-container">
          <FaSpinner className="spinning" />
          <p>Chargement de la facture...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modifier-facture-page">
        <div className="title-badge">
          <FaFileInvoice className="page-title-icon" aria-hidden="true" />
          <span>ERREUR</span>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button
            onClick={() => navigate("/factures")}
            className="back-button"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modifier-facture-page">
      <div className="title-badge">
        <FaFileInvoice className="page-title-icon" aria-hidden="true" />
        <span>MODIFIER UNE FACTURE</span>
      </div>

      <form onSubmit={handleSubmit} className="modifier-facture-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="numero">
              {t.invoiceNumberLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="numero"
              name="numero"
              placeholder={t.invoiceNumberPlaceholder}
              value={formData.numero}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.numero && touchedFields.numero ? "input-error" : ""}
              maxLength={100}
            />
            {fieldErrors.numero && touchedFields.numero && (
              <span className="field-error-message">{fieldErrors.numero}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="fournisseur">
              {t.supplier} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="fournisseur"
              name="fournisseur"
              placeholder={t.supplierName}
              value={formData.fournisseur}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.fournisseur && touchedFields.fournisseur ? "input-error" : ""}
              maxLength={255}
            />
            {fieldErrors.fournisseur && touchedFields.fournisseur && (
              <span className="field-error-message">{fieldErrors.fournisseur}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date_achat">
              {t.purchaseDateLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date_achat"
              name="date_achat"
              value={formData.date_achat}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_achat && touchedFields.date_achat ? "input-error" : ""}
            />
            {fieldErrors.date_achat && touchedFields.date_achat && (
              <span className="field-error-message">{fieldErrors.date_achat}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="montant_total">
              {t.totalAmountLabel} (FCFA) <span className="required-indicator">*</span>
            </label>
            <input
              type="number"
              id="montant_total"
              name="montant_total"
              placeholder="0.00"
              step="0.01"
              value={formData.montant_total}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.montant_total && touchedFields.montant_total ? "input-error" : ""}
            />
            {fieldErrors.montant_total && touchedFields.montant_total && (
              <span className="field-error-message">{fieldErrors.montant_total}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              placeholder={t.additionalNotes}
              value={formData.notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form full-width">
            <label htmlFor="facture_image">Image de la facture</label>
            <input
              type="file"
              id="facture_image"
              name="facture_image"
              accept="image/*"
              onChange={handleFileChange}
            />
            {formData.facture_image && (
              <p className="file-name">Nouvelle image: {formData.facture_image.name}</p>
            )}
            {initialData?.facture_image && !formData.facture_image && (
              <p className="file-name">Image actuelle disponible</p>
            )}
          </div>
        </div>

        <div className="actions">
          <button
            type="button"
            className="reset-but"
            onClick={handleReset}
            disabled={!hasChanges() || isSubmitting}
            title="Réinitialiser le formulaire"
          >
            <FaRedo />
            <span>Réinitialiser</span>
          </button>
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || isSubmitting || !hasChanges()}
          >
            <FaCheck />
            <span>{isSubmitting ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/factures")}
            disabled={isSubmitting}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer la modification</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer la modification :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">{t.invoiceNumber}:</span>
                  <span className="summary-value">{formData.numero}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Fournisseur :</span>
                  <span className="summary-value">{formData.fournisseur}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.purchaseDate}:</span>
                  <span className="summary-value">{parseLocalDate(formData.date_achat)?.toLocaleDateString('fr-FR') || formData.date_achat}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.totalAmountLabel}:</span>
                  <span className="summary-value">{parseFloat(formData.montant_total).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={isSubmitting}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={isSubmitting}
              >
                <FaCheck />
                <span>{isSubmitting ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}